import React, { useCallback, useEffect, useState } from 'react';
import { Button, Row } from 'reactstrap';
import { Option, StyleOption } from '../../styled';
import DefaultModal from '../../../../../../../components/Modal/DefaultModal';
import LeadInputs from 'views/CRM/Lead/form/FormInputs';
import OpportunityInputs from 'views/CRM/Opportunity/form/FormInputs';
import { useRecordTypesByUser } from 'views/RecordType/hooks/useRecordTypesByUser';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import ReactLoader from 'components/Loader';
import RecordTypeSelection from 'views/RecordType/components/RecordTypeSelection';
import { LeadsSelection } from './components/LeadsSelection';
import { OpportunitiesSelection } from './components/OpportunitiesSelection';
import AssociationConfirmationModal from './components/AssociationConfirmationModal';
import { toast } from 'react-toastify';
import AssociationService from '../../../../../services/associationService';
import { useConversationAssociations } from '../../../../../hooks/useConversationAssociations';
import { HiUserAdd } from 'react-icons/hi';
import { FaCheckCircle } from 'react-icons/fa';

export const ModalLeadOpportunity = ({ conversation = {}, contactId = {} }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [shouldShowLeadForm, setShouldShowLeadForm] = useState(false);
  const [shouldShowOpportunityForm, setShouldShowOpportunityForm] =
    useState(false);
  const [shouldShowLeadsSelection, setShouldShowLeadsSelection] =
    useState(false);
  const [
    shouldShowOpportunitiesSelection,
    setShouldShowOpportunitiesSelection
  ] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [selectedRecordTypeId, setSelectedRecordTypeId] = useState(null);
  const [formType, setFormType] = useState(null);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedOpportunities, setSelectedOpportunities] = useState([]);
  const [selectedLeadsData, setSelectedLeadsData] = useState([]);
  const [selectedOpportunitiesData, setSelectedOpportunitiesData] = useState(
    []
  );
  const [leadsRecordTypeId, setLeadsRecordTypeId] = useState(null);
  const [opportunitiesRecordTypeId, setOpportunitiesRecordTypeId] =
    useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const { recordTypes, isLoadingRecordTypes, getRecordTypesByUser } =
    useRecordTypesByUser(
      formType === 'lead' || formType === 'associateLeads'
        ? entitiesName.LEAD
        : entitiesName.OPPORTUNITY
    );

  // Hook para gerenciar verificação de associações
  const {
    associations,
    isLoading: isLoadingAssociations,
    hasLeadAssociations,
    hasOpportunityAssociations,
    checkAssociations,
    clearAssociations,
    refreshAssociations
  } = useConversationAssociations(conversation._id || conversation.id);

  useEffect(() => {
    if (isModalOpen) {
      getRecordTypesByUser();
      checkAssociations();
      return;
    }
    // Reset de todos os estados quando o modal fecha
    setShouldShowLeadForm(false);
    setShouldShowOpportunityForm(false);
    setShouldShowLeadsSelection(false);
    setShouldShowOpportunitiesSelection(false);
    setShowConfirmationModal(false);
    setFormType(null);
    setSelectedLeads([]);
    setSelectedOpportunities([]);
    setSelectedLeadsData([]);
    setSelectedOpportunitiesData([]);
    setLeadsRecordTypeId(null);
    setOpportunitiesRecordTypeId(null);
    setIsProcessing(false);
    clearAssociations();
  }, [isModalOpen, getRecordTypesByUser, checkAssociations, clearAssociations]);

  useEffect(() => {
    if (recordTypes && recordTypes.length > 0) {
      setSelectedRecordTypeId(recordTypes[0].id);
    }
  }, [recordTypes]);

  // Efeito para iniciar automaticamente a seleção quando o formType é de associação
  useEffect(() => {
    if (
      (formType === 'associateLeads' ||
        formType === 'associateOpportunities') &&
      selectedRecordTypeId
    ) {
      handleOnSelectedRecordType(selectedRecordTypeId);
    }
  }, [formType, selectedRecordTypeId, handleOnSelectedRecordType]);

  const handleOnSelectedRecordType = useCallback(
    (recordTypeId) => {
      setSelectedRecordTypeId(recordTypeId);
      if (formType === 'lead') {
        setShouldShowLeadForm(true);
        setShouldShowOpportunityForm(false);
        setShouldShowLeadsSelection(false);
        setShouldShowOpportunitiesSelection(false);
      } else if (formType === 'opportunity') {
        setShouldShowOpportunityForm(true);
        setShouldShowLeadForm(false);
        setShouldShowLeadsSelection(false);
        setShouldShowOpportunitiesSelection(false);
      } else if (formType === 'associateLeads') {
        setShouldShowLeadsSelection(true);
        setShouldShowLeadForm(false);
        setShouldShowOpportunityForm(false);
        setShouldShowOpportunitiesSelection(false);
      } else if (formType === 'associateOpportunities') {
        setShouldShowOpportunitiesSelection(true);
        setShouldShowLeadForm(false);
        setShouldShowOpportunityForm(false);
        setShouldShowLeadsSelection(false);
      }
    },
    [formType]
  );

  // Função chamada quando avança dos leads
  const handleLeadsAdvance = (leads, recordTypeId, leadsData) => {
    setSelectedLeads(leads);
    setSelectedLeadsData(leadsData || []);
    setLeadsRecordTypeId(recordTypeId);
    setShowConfirmationModal(true);
  };

  // Função chamada quando avança das oportunidades
  const handleOpportunitiesAdvance = (
    opportunities,
    recordTypeId,
    opportunitiesData
  ) => {
    setSelectedOpportunities(opportunities);
    setSelectedOpportunitiesData(opportunitiesData || []);
    setOpportunitiesRecordTypeId(recordTypeId);
    setShowConfirmationModal(true);
  };

  // Função para finalizar associação
  const handleFinalize = async () => {
    if (selectedLeads.length === 0 && selectedOpportunities.length === 0) {
      toast.warning(
        'Selecione pelo menos um lead ou uma oportunidade para finalizar'
      );
      return;
    }

    setIsProcessing(true);
    try {
      const data = {
        conversationId: conversation._id || conversation.id,
        conversationInAttendanceId: conversation._id || conversation.id,
        channel: conversation.canal,
        attendantName: localStorage.getItem('name'),
        attendantLogin: localStorage.getItem('login'),
        clientName: conversation.cliente?.nome,
        clientPhone: conversation.cliente?.telefone,
        leadIds: selectedLeads,
        opportunityIds: selectedOpportunities,
        leadsRecordTypeId: leadsRecordTypeId,
        opportunitiesRecordTypeId: opportunitiesRecordTypeId
      };

      // Chamada para nova API
      await AssociationService.associateLeadOpportunity(data);

      const successMessage =
        formType === 'associateLeads'
          ? 'Lead associado com sucesso!'
          : formType === 'associateOpportunities'
          ? 'Oportunidade associada com sucesso!'
          : 'Leads e oportunidades associados com sucesso!';

      toast.success(successMessage);

      await refreshAssociations();
      setIsModalOpen(false);
    } catch (error) {
      toast.error(
        error?.response?.data?.message || 'Erro ao associar. Tente novamente.'
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const shouldShowRecordTypeSelection =
    isLoadingRecordTypes ||
    (!shouldShowLeadForm &&
      !shouldShowOpportunityForm &&
      !shouldShowLeadsSelection &&
      !shouldShowOpportunitiesSelection);

  return (
    <>
      <Option title={'Criar Lead/Oportunidade'}>
        <Button
          color="link"
          size="sm"
          style={StyleOption}
          onClick={() => setIsModalOpen(true)}
        >
          <HiUserAdd size={20} color="#32325d" />
        </Button>
      </Option>

      <DefaultModal
        show={isModalOpen}
        onModalToggle={setIsModalOpen}
        title={`${
          formType === 'lead'
            ? 'Adicionar Lead'
            : formType === 'opportunity'
            ? 'Adicionar Oportunidade'
            : formType === 'associateLeads'
            ? 'Associar Leads'
            : formType === 'associateOpportunities'
            ? 'Associar Oportunidades'
            : 'Criar Lead/Oportunidade'
        }`}
        showCloseButton={false}
        modalContainerClasses="modal-lg modal-dialog-scrollable"
      >
        <Row
          className="justify-content-md-center"
          style={{ marginBottom: '20px' }}
        >
          {!shouldShowLeadForm &&
            !shouldShowOpportunityForm &&
            !shouldShowLeadsSelection &&
            !shouldShowOpportunitiesSelection && (
              <>
                <Button
                  color="primary"
                  onClick={() => {
                    if (hasLeadAssociations) {
                      toast.warning(
                        'Já existe um lead associado a esta conversa.'
                      );
                      return;
                    }
                    if (hasOpportunityAssociations) {
                      toast.warning(
                        'Já existe uma oportunidade associada a esta conversa. Não é possível criar um lead.'
                      );
                      return;
                    }
                    setFormType('lead');
                  }}
                  disabled={
                    formType === 'lead' ||
                    hasLeadAssociations ||
                    hasOpportunityAssociations ||
                    isLoadingAssociations
                  }
                  className="me-2 mb-2"
                  size="sm"
                  title={
                    hasLeadAssociations
                      ? 'Já existe um lead associado a esta conversa.'
                      : hasOpportunityAssociations
                      ? 'Já existe uma oportunidade associada. Não é possível criar lead.'
                      : 'Criar um novo lead'
                  }
                >
                  Criar Lead
                </Button>
                <Button
                  color="primary"
                  onClick={() => {
                    if (hasLeadAssociations) {
                      toast.warning(
                        'Já existe um lead associado a esta conversa. A oportunidade deve ser criada pela conversão do lead.'
                      );
                      return;
                    }
                    if (hasOpportunityAssociations) {
                      toast.warning(
                        'Já existe uma oportunidade associada a esta conversa.'
                      );
                      return;
                    }
                    setFormType('opportunity');
                  }}
                  disabled={
                    formType === 'opportunity' ||
                    hasLeadAssociations ||
                    hasOpportunityAssociations ||
                    isLoadingAssociations
                  }
                  className="me-2 mb-2"
                  size="sm"
                  title={
                    hasLeadAssociations
                      ? 'Já existe um lead associado. A oportunidade deve vir pela conversão do lead.'
                      : hasOpportunityAssociations
                      ? 'Já existe uma oportunidade associada a esta conversa.'
                      : 'Criar uma nova oportunidade'
                  }
                >
                  Criar Oportunidade
                </Button>
                <Button
                  color="success"
                  onClick={() => {
                    if (hasLeadAssociations) {
                      toast.warning(
                        'Já existe um lead associado a esta conversa.'
                      );
                      return;
                    }
                    if (hasOpportunityAssociations) {
                      toast.warning(
                        'Já existe uma oportunidade associada a esta conversa. Não é possível associar um lead.'
                      );
                      return;
                    }
                    setFormType('associateLeads');
                  }}
                  disabled={
                    hasLeadAssociations ||
                    hasOpportunityAssociations ||
                    isLoadingAssociations
                  }
                  className="me-2 mb-2"
                  size="sm"
                  title={
                    hasLeadAssociations
                      ? 'Já existe um lead associado a esta conversa.'
                      : hasOpportunityAssociations
                      ? 'Já existe uma oportunidade associada. Não é possível associar lead.'
                      : 'Associar um lead a este contato'
                  }
                >
                  {hasLeadAssociations ? (
                    <>
                      <FaCheckCircle size={14} className="me-1" />
                      Lead Já Associado
                    </>
                  ) : (
                    'Associar Leads'
                  )}
                </Button>
                <Button
                  color="success"
                  onClick={() => {
                    if (hasLeadAssociations) {
                      toast.warning(
                        'Já existe um lead associado a esta conversa. A oportunidade deve ser criada pela conversão do lead.'
                      );
                      return;
                    }
                    if (hasOpportunityAssociations) {
                      toast.warning(
                        'Já existe uma oportunidade associada a esta conversa.'
                      );
                      return;
                    }
                    setFormType('associateOpportunities');
                  }}
                  disabled={
                    hasLeadAssociations ||
                    hasOpportunityAssociations ||
                    isLoadingAssociations
                  }
                  className="mb-2"
                  size="sm"
                  title={
                    hasLeadAssociations
                      ? 'Já existe um lead associado. A oportunidade deve vir pela conversão do lead.'
                      : hasOpportunityAssociations
                      ? 'Já existe uma oportunidade associada a esta conversa.'
                      : 'Associar uma oportunidade a este contato'
                  }
                >
                  {hasOpportunityAssociations ? (
                    <>
                      <FaCheckCircle size={14} className="me-1" />
                      Oportunidade Já Associada
                    </>
                  ) : (
                    'Associar Oportunidades'
                  )}
                </Button>
              </>
            )}
        </Row>

        {isLoadingRecordTypes || !selectedRecordTypeId ? (
          <Row className="justify-content-md-center">
            <ReactLoader />
          </Row>
        ) : (
          <>
            {shouldShowRecordTypeSelection &&
            formType &&
            formType !== 'associateLeads' &&
            formType !== 'associateOpportunities' ? (
              <RecordTypeSelection
                recordTypes={recordTypes}
                handleOnSelectedRecordType={handleOnSelectedRecordType}
              />
            ) : (
              <>
                {formType === 'lead' && (
                  <LeadInputs
                    recordTypeId={selectedRecordTypeId}
                    isModal={true}
                    callbackFunc={() => setIsModalOpen(false)}
                    conversation={conversation}
                  />
                )}
                {formType === 'opportunity' && (
                  <OpportunityInputs
                    recordTypeId={selectedRecordTypeId}
                    isModal={true}
                    callbackFunc={() => setIsModalOpen(false)}
                    conversation={conversation}
                    contactId={contactId}
                    isAttendace={true}
                  />
                )}
                {formType === 'associateLeads' && shouldShowLeadsSelection && (
                  <LeadsSelection
                    conversation={conversation}
                    onClose={() => setIsModalOpen(false)}
                    onAdvance={handleLeadsAdvance}
                    selectedLeads={selectedLeads}
                    associations={associations}
                  />
                )}
                {formType === 'associateOpportunities' &&
                  shouldShowOpportunitiesSelection && (
                    <OpportunitiesSelection
                      conversation={conversation}
                      onClose={() => setIsModalOpen(false)}
                      onAdvance={handleOpportunitiesAdvance}
                      selectedOpportunities={selectedOpportunities}
                      associations={associations}
                    />
                  )}
              </>
            )}
          </>
        )}
      </DefaultModal>

      <AssociationConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onFinalize={handleFinalize}
        isProcessing={isProcessing}
        formType={formType}
        selectedLeads={selectedLeads}
        selectedOpportunities={selectedOpportunities}
        selectedLeadsData={selectedLeadsData}
        selectedOpportunitiesData={selectedOpportunitiesData}
      />
    </>
  );
};
